/* tslint:disable:import-spacing component-class-suffix no-inferrable-types variable-name
   typedef-whitespace component-selector no-trailing-whitespace */

import {
  AfterViewInit,
  OnDestroy,
  Component,
  HostListener,
  Input
}                                    from '@angular/core';

import {
  BehaviorSubject,
  Subscription
}                                    from 'rxjs';

import {
  WorkBlock,
  WorkType
}                                    from '@/models/scenario/work.block';

import {Period}                      from '@/util/period';
import {Scenario}                    from '@/models/scenario/scenario';
import {Logger}                      from '@/util/logger';
import {UnDoRedo}                    from '@/util/un.do.redo';
import {WorkTypeRenderer}            from './work.type.renderer';
import {NameRenderer}                from '@/helpers/name.renderer';
import {WorkDecisionPointRenderer}   from './work.decision.point.renderer';
import {PeriodRenderer}              from '@/helpers/period.renderer';
import {WorkBlockChange}             from '@/screens/ide/workblox/work.block.change';
import {SummaryTableSelectCommand}   from './summary.table.select.command';
import {SummaryTableUnSelectCommand} from './summary.table.un.select.command';
import {WorkBlockInlineEdit}         from './work.block.inline.edit';
import {ICommand}                    from '@/util/i.command';

import 'ag-grid-community';

import {
  GridApi,
  RowClickedEvent,
  IRowNode,
  GridReadyEvent,
  GetContextMenuItemsParams, CsvExportParams, ProcessCellForExportParams, ExcelExportParams
} from "ag-grid-community";
import {basename} from "@/util/util";
import {Train} from "@/models/scenario/train";
import {TrainClass} from "@/models/scenario/train.class";
import {DecisionPoint} from "@/models/scenario/decision.point";

@Component({
  selector: 'work-blox-summary-table',
  template: `
    <div id="work-blox-table-container_{{uuid}}" class="work-blox-table-container" style="">
      <ag-grid-angular id="ag-grid-work-blox-table_{{uuid}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-work-blox-table"
        [rowData]="rowData"
        [columnDefs]="defaultColDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"
        [rowSelection]="'single'"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        (gridReady)="onGridReady($event)"
        (selectionChanged)="onSelectionChanged()"
        (rowClicked)="onRowClicked($event)"
      >
      </ag-grid-angular>
    </div>
    `
})

/**
 *
 * @class WorkBloxSummaryTable
 *
 * @brief @brief this table shows a tabular summary of all work blocks in the current
 * scenario.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class WorkBloxSummaryTable implements AfterViewInit, OnDestroy {

  @Input() uuid: number = 0;

  /** the history manager (not owned by us */
  private _history: UnDoRedo     = null;

  /** @var _scenario the scenario model */
  private _scenario: Scenario = null;

  /** the currently active row/selection/workblock */
  private selectedWorkBlock: BehaviorSubject<WorkBlockChange>     = null;
  private         selectSub: Subscription                         = null;

  private    lastInlineEdit: BehaviorSubject<WorkBlockInlineEdit> = null;
  private           editSub: Subscription                         = null;

  /* the row that was previously selected */
  private oldSelection: any = null;

  /** the parent controller/view must provide us with a means to capture the editor input */
  public inputCapture: () => WorkBlockChange;

  /** the parent controller/view must provide usu with a means to restore the editor input */
  public inputRestore: (snapshot: WorkBlockChange) => void;

  /** the API for the widget */
  private          api: GridApi  = null;

  private isFullScreen: boolean = false;

  public context = {

    workBloxTable: this

  };

  public defaultColDef = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData = [];


  public defaultColDefs: any = [

    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      width: 72
    },

    {
      headerName: 'Name',
      field: 'name',
      cellRenderer: NameRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Type',
      children: [
        {
          headerName: 'ROW',
          field: 'row',
          hide: true
        },
        {
          headerName: 'Type',
          field: 'type',
          cellRenderer: WorkTypeRenderer,
          editable: false,
          width: 150
        },
      ]
    },

    {
      headerName: 'Start',
      children: [
        {
          headerName: 'Location',
          field: 'start_loc',
          cellRenderer: WorkDecisionPointRenderer,
          editable: false,
          width: 150
        },
        {
          headerName: 'Time',
          field: 'start_time',
          cellRenderer: PeriodRenderer,
          editable: false,
          width: 150,
          columnGroupShow: 'open'
        }
      ]
    },

    {
      headerName: 'End',
      children: [
        {
          headerName: 'Location',
          field: 'end_loc',
          cellRenderer: WorkDecisionPointRenderer,
          editable: false,
          width: 150
        },
        {
          headerName: 'Time',
          field: 'end_time',
          cellRenderer: PeriodRenderer,
          editable: false,
          width: 150,
          columnGroupShow: 'open'
        }
      ]
    }

  ];

  public onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;
  }

  /** helper to fetch the actual WorkBlock for the given row */
  public blockOfRow(data: any): WorkBlock {

    const id = data?.id ? data.id : 0;

    return Array.from(this._scenario.workBlocks().values()).
      find(x => x._id === id);
  }

  /** allow selection from history */
  public doSelection(selection: WorkBlockChange) {

    /*
     * to allow user inputs to sometimes be different from the current
     * selection; i.e. no selection in summary view, and the user is
     * editing a completely new block...we use the 'snapshot' field; when
     * we emit a work block change...the input editor will look for the
     * snapshot field as an override; if its there, it uses those values
     * rather than the thing being selected.
     *
     * the upshot is that all we have to, is emit the selection (for the
     * other sub-panels), and locally select the implied row...or clear
     * the selection if there is nothing selected.
     *
     * We don't have to worry about interaction / timing with the other agGrid
     * events...because we haven't added any mechanics there.  It's all done
     * via RowClicked event, effectively doSelection() is  fake user click
     * on the row...but from history.
     *
     */

    /* adjust the summary table selection to this point in the history */

    if ((selection === null) || (selection?.id === null) || isNaN(selection.id)) {

      /* there is no row selected, just clear the selection */

      this.api.deselectAll();

      this.oldSelection = null;

    } else {

      /* select the implied row */

      const block: WorkBlock = this.blockOfRow(selection);

      this.api.forEachNode((node: IRowNode): void => {

        if (node.data.id !== block._id) {

          return;
        }

        node.setSelected(true, true);

        this.oldSelection = node.data;

      });

    }

    /* emit the selection so that other panels also sync up with this position in the history */

    this.selectedWorkBlock.next(selection);

  }

  /** fetch the current selection */
  public getSelection(): WorkBlockChange {

    const  data: WorkBlockChange = new WorkBlockChange({
      src:         this,
      id:          null,
      name:        null,
      type:        null,
      start:       null,
      end:         null,
      begin:       null,
      duration:    null,
      days:        null,
      description: null,
      snapshot:    this.inputCapture(),
      model:       null
    });

    /* is anything selected? */

    if (this.oldSelection === null) {

      /* we have the selection */

      return data;
    }

    /* if something is selected, then we fill in selection */

    const block: WorkBlock = this.blockOfRow(this.oldSelection);

    data.src               = this;
    data.id                = block._id;
    data.name              = block._name;
    data.type              = block._type;
    data.start             = block._from;
    data.end               = block._to;
    data.begin             = new Period(Period.toString(block._start));
    data.duration          = new Period(Period.toString(block._duration));
    data.days              = block._days;
    data.description       = block._description;

    return data;
  }

  /** clear the selection (if any) */
  public clearSelection() {

    if (this.oldSelection === null) {

      /* nothing to do */

      return ;
    }

    /* the old value (the existing selection) might be empty */

    const  empty: WorkBlockChange = new WorkBlockChange({
      src:         this,
      id:          null,
      name:        null,
      type:        null,
      start:       null,
      end:         null,
      begin:       null,
      duration:    null,
      days:        null,
      description: null,
      snapshot:    null
    });

    let oldValue: WorkBlockChange;
    let newValue: WorkBlockChange;
    let block:    WorkBlock;

    /* capture the old value */

    /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

    block    = this.blockOfRow(this.oldSelection);
    oldValue = new WorkBlockChange({
      src:         this,
      id:          block._id,
      name:        block._name,
      type:        block._type,
      start:       block._from,
      end:         block._to,
      begin:       new Period(Period.toString(block._start)),
      duration:    new Period(Period.toString(block._duration)),
      days:        block._days,
      description: block._description,
      snapshot:    this.inputCapture()
    });

    /* capture the new value */

    newValue = empty;

    /* de-select */

    this.api.deselectAll();

    this.oldSelection = null;

    /* it's now safe to signal the selection change */

    this.selectedWorkBlock.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableUnSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  /**
   *  handle a user clicking a row, which just toggles it to selected or no selection at all
   *  because we only allow single row selection.
   *
   */
  public onRowClicked(e: RowClickedEvent) {

    /* the old value (the existing selection) might be empty */

    const  empty: WorkBlockChange = new WorkBlockChange({
      src:         this,
      id:          null,
      name:        null,
      type:        null,
      start:       null,
      end:         null,
      begin:       null,
      duration:    null,
      days:        null,
      description: null,
      snapshot:    null
    });

    let oldValue: WorkBlockChange;
    let newValue: WorkBlockChange;
    let block:    WorkBlock       = null;

    /* we only do single select, so if the row we click on is already selected...we are deselecting */

    if ((this.oldSelection !== null) && (this.oldSelection.id === e.data.id)) {

      /* capture the old value */

      /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

      block    = this.blockOfRow(e.data);
      oldValue = new WorkBlockChange({
        src:         this,
        id:          block._id,
        name:        block._name,
        type:        block._type,
        start:       block._from,
        end:         block._to,
        begin:       new Period(Period.toString(block._start)),
        duration:    new Period(Period.toString(block._duration)),
        days:        block._days,
        description: block._description,
        snapshot:    this.inputCapture()
      });

      /* capture the new value */

      newValue = empty;

      /* de-select */

      this.api.deselectAll();

      this.oldSelection = null;

    }  else {

      /* capture the old state */

      if (this.oldSelection === null) {

        oldValue          = Object.assign({}, empty);
        oldValue.snapshot = this.inputCapture();

      } else {

        /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

        block     = this.blockOfRow(this.oldSelection);
        oldValue  = new WorkBlockChange({
          src:         this,
          id:          block._id,
          name:        block._name,
          type:        block._type,
          start:       block._from,
          end:         block._to,
          begin:       new Period(Period.toString(block._start)),
          duration:    new Period(Period.toString(block._duration)),
          days:        block._days,
          description: block._description,
          snapshot:    this.inputCapture()
        });

        /*  agGrid will do its usual selection change events */
      }

      /* capture the new state, when snapshot is null the inputs will be newValue */

      block    = this.blockOfRow(e.data);
      newValue = new WorkBlockChange({
        src:         this,
        id:          block._id,
        name:        block._name,
        type:        block._type,
        start:       block._from,
        end:         block._to,
        begin:       new Period(Period.toString(block._start)),
        duration:    new Period(Period.toString(block._duration)),
        days:        block._days,
        description: block._description,
        snapshot:    null
      });

      this.oldSelection = e.data;
    }

    /* it's now safe to signal the selection change */

    this.selectedWorkBlock.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  public onSelectionChanged(): void {

    if (this.oldSelection === null) {

      return;
    }

    /* keep the currently selected row selected */

    this.api.forEachNode((node: IRowNode): void => {

      if (node.data.id === this.oldSelection.id) {

        node.setSelected(true, true);
      }

    });
  }

  public toggleFullScreen() {

    if (this.isFullScreen) {

      Logger.debug('[workblox/table] exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[workblox/table] going full screen...');

      const elem = document.getElementById(`ag-grid-work-blox-table_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    const     context: any[] = [];
    const workBloxTable      = params.context.workBloxTable;

    if (context.length !== 0) {

      context.push('separator');
    }

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (workBloxTable.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: (): void => {

        workBloxTable.toggleFullScreen();
      }
    });

    context.push('separator');

    const general: any[] = [

      'copy',
      'copyWithHeaders',
      'paste',

      'separator',

      'expandAll',
      'contractAll',

      'separator',
    ];

    context.push.apply(context, general);

    /* add in our custom export functions ... */

    context.push( {
      name: `CSV Export`,
      action: (): void => {

        workBloxTable.csvExport();
      }
    });

    context.push( {
      name: `Excel Export`,
      action: (): void => {

        workBloxTable.excelExport();
      }
    });

    /* add remaining common / generic actions */

    const general2: any[] = [

      'separator',

      'autoSizeAll'
    ];

    /* pass back the merged menu */

    context.push.apply(context, general2);

    return context;
  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** @brief first time setup of view */
  ngAfterViewInit(): void {

    Logger.debug('[workblox/table] view init...');

    this.clear();

    const          elem: HTMLElement          = document.getElementById(`ag-grid-work-blox-table_${this.uuid}`);
    const workBloxTable: WorkBloxSummaryTable = this;

    elem.addEventListener('fullscreenchange', () => {

      workBloxTable.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[workblox/table] (elem) flipped full screen mode: ', workBloxTable.isFullScreen);

    }, true);
  }

  /** @brief reset the view to empty */
  public clear(): void {

    this._scenario = null;
  }

  /** @brief standard destructor */
  destroy() {

    Logger.debug('[workblox/table] cleaning up...');

  }

  /** format one of the table column values as a simple string (i.e. for export) */
  public format(value: any): string {

    if (typeof value === 'object' && value.hasOwnProperty('name') && value.hasOwnProperty('code')) {

      /* it the type field, just return type name */

      return value['name'];
    }

    if (value instanceof DecisionPoint) {

      /* this is the start or end point */

      let   name: string = value._name.trim();
      const mile: string = value._mileage.toFixed(2);

      if (name.length >= 16) {

        name = name.substring(0, 16) + '...';
      }

      return `${name} - ${mile}mi`;
    }

    if (value instanceof Period) {

      const d = PeriodRenderer.pad(value._days);
      const h = PeriodRenderer.pad(value._hours);
      const m = PeriodRenderer.pad(value._minutes);
      const s = PeriodRenderer.pad(value._secs);

      return`${d}:${h}:${m}:${s}`;
    }

    if (value instanceof TrainClass) {

      return value._name;
    }

    if (Array.isArray(value) && value.length > 0 && value[0] instanceof TrainClass) {

      return (value as TrainClass[]).map((tc: TrainClass): string => {

        return tc._name;

      }).join(',');
    }

    /* fell through, we don't know how to format this thing. */

    Logger.error('[workblox/table] can not format cell', value);

    return '';
  }

  /** export the table to a CSV download */
  public csvExport(): void {

    let wst: WorkBloxSummaryTable = this;

    Logger.debug('[workblox/table] CSV export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(wst._scenario._tag)}.csv`;

    let params: CsvExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return wst.format(params.value);
      }
    };

    wst.api.exportDataAsCsv(params);
  }

  /** export the table to an Excel download */
  public excelExport(): void {

    let wst: WorkBloxSummaryTable = this;

    Logger.debug('[workblox/table] EXCEL export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(wst._scenario._tag)}.xlsx`;

    let params: ExcelExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return wst.format(params.value);
      }
    };

    wst.api.exportDataAsExcel(params);
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** @brief set the table base on this scenario */
  setScenario(scenario: Scenario = null): boolean {

    Logger.debug('[workblox/table] setting scenario...');

    if (scenario === null) {

      this.clear();

      return true;
    }

    /* save the model */

    this._scenario                   = scenario;

    /* refresh the table based on currently selected train class */

    this.refreshTable();

    /* all done */

    return true;
  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<WorkBlockInlineEdit>): void {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = edit.subscribe( (next: WorkBlockInlineEdit): void => {

      this.inlineEdited(next);
    });
  }

  /** handle single column changes in the editing inputs */
  public inlineEdited(edit: WorkBlockInlineEdit) {

    if (edit === null) {

      /* nothing to do */

      return ;
    }

    Logger.debug('[workblox/table] inline edit', edit);

    let datum: any = edit.value;

    /* if the change was to the days...we're going to have to refresh everything */

    if (edit.field === 'days') {

      /* rebuild the table */

      this.refreshTable();

      /*
       * rebuilding the table, clears the selection...it's a new table...so we
       * need to re-select the row that is logically selected.  We can't just
       * do it here, because agGrid has to go through event based setup.  We
       * have no way of knowing when it won't overwrite our attempt to set the
       * selection.
       *
       * To work around that, we register with the AnimationQueueEmpty event, and
       * just force the selection to be what it was before we refreshed the
       * table.
       *
       */

      return ;
    }

    /* updating start means both start and end times must be re-calculated */

    if ((edit.field === 'start') || (edit.field === 'duration')) {

      /*
       * note: the editor updated the underlying model before firing events, so we can just
       * use the data in the model.
       *
       */

      this.api.forEachNode((node: IRowNode): void => {

        if (node.data.id !== edit.id) {

          return;
        }

        /* twitch the value (without any change events) */

        const block: WorkBlock  = this.blockOfRow(node.data);

        node.data['start_time'] = new Period(Period.toString(block._start));
        node.data['end_time']   = new Period(Period.toString(block._start + block._duration));

        this.api.redrawRows({rowNodes: [node]});

      });

      return ;
    }

    if (edit.field === 'type') {

      if (edit.value === WorkType.MEGABLOCK) {

        datum = {name: 'MegaBlock', code: WorkType.MEGABLOCK};

      } else if (edit.value === WorkType.CONSTRUCTION) {

        datum = {name: 'Construction', code: WorkType.CONSTRUCTION};

      } else {

        datum = '';
      }
    }

    /* ok, just a normal inline edit, twitch our value */

    let dataField: string = '';

    switch (edit.field) {

      case 'type':
      case 'name':
      case 'days':
        {
          dataField = edit.field;
        }
        break;

      case 'description':
        {
          dataField = 'desc';
        }
        break;

      case 'from':
        {
          dataField = 'start_loc';
        }
        break;

      case 'to':
        {
          dataField = 'end_loc';
        }
        break;

    }

    /* get the row for this work block */

    this.api.forEachNode((node: IRowNode): void => {

      if (node.data.id !== edit.id) {

        return;
      }

      /* twitch the value (without any change events) */

      node.data[dataField] = datum;

      this.api.redrawRows({rowNodes: [node]});

    });

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<WorkBlockChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedWorkBlock = selection;
    this.selectSub         = selection.subscribe( (next: WorkBlockChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: WorkBlockChange) {

    if (change === null) {

      /* just clear the selection */

      Logger.debug('[workblox/table:set] clearing selection');

      this.api.deselectAll();
      this.oldSelection = null;

      return ;
    }

    /* check that we aren't calling ourselves */

    if ( (change.src instanceof WorkBloxSummaryTable) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

  }

  /** @brief helper to update the table  */
  public refreshTable(): boolean {

    Logger.debug('[workblox/table] refreshing on', this._scenario);

    /* start with the non-active day columns ... */

    const cd: any[] = [].concat([], this.defaultColDefs);

    /* we need a column for each active day in any of the work blocks */

    const days: number[]    = [];

    /* pre-scan work blocks to know what all the active days are. */

    for (const block of this._scenario.workBlocks().values()) {

      for (const d of block._days) {

        if (days.includes(d)) {

          continue;
        }

        days.push(d);
      }

    } /* end of pre-scan for day numbers */

    /* make sure they are in order */

    days.sort((a: number, b: number) => {

      return a - b;
    });

    /* ok, append a column (in order) for each active day */

    for (const day of days) {

      cd.push({
        headerName: `${day}`,
        field:      `day_${day}`,
        editable:   false,
        width:      64
      });

    }

    /* ok, we can now tell agGrid what our columns are ... */

    this.api.setGridOption('rowData', []);
    this.api.setGridOption('columnDefs', cd);

    /* now just build out the row data ... */

    this.rowData = [];

    let row: number = 0;

    for (const block of this._scenario.workBlocks().values()) {

      /* everything except active days */

      const typeValue =  (block._type === WorkType.MEGABLOCK) ?
        {name: 'MegaBlock',    code: WorkType.MEGABLOCK} :
        {name: 'Construction', code: WorkType.CONSTRUCTION};

      const rd: any = {
        id:         block._id,
        name:       block._name,
        row,
        type:       typeValue,
        start_loc:  block._from,
        start_time: new Period(Period.toString(block._start)),
        end_loc:    block._to,
        end_time:   new Period(Period.toString(block._start + block._duration)),

      };

      row++;

      /* default fill for active days */

      for (const dd of days) {

        rd[`day_${dd}`] = '';

      }

      /* fill active days */

      for (const ad of block._days) {

        rd[`day_${ad}`] = '\u2A09';

      }

      this.rowData.push(rd);

    } /* end of work block walk */

    this.api.setGridOption('rowData', this.rowData);

    /* all done */

    return true;
  }

  @HostListener('window:resize')
  public resize() {

    Logger.debug('[workblox/table]', 'resizing...');

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */
  }

}
